import React, { useMemo, useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { Eye, EyeOff, Users } from 'lucide-react';
import Swal from 'sweetalert2';
import { db } from '../../firebase.js';
import { useAdminContext } from '../../context/AdminContext.js';

const MAX_RESULTS = 20;

/**
 * Habilita/oculta las secciones "Equipo de Trabajo" y "Plan de Carrera" en el panel de cada usuario
 * (campo canSeeTeamAndCareer del perfil; solo un admin puede escribirlo, ver firestore.rules).
 * Sin búsqueda muestra los habilitados; con búsqueda, los usuarios que coinciden.
 */
const TeamCareerAccess = () => {
    const { users } = useAdminContext() || { users: [] };
    const [search, setSearch] = useState('');
    const [processingId, setProcessingId] = useState(null);

    const enabledUsers = useMemo(() => (users || []).filter(u => u.canSeeTeamAndCareer === true), [users]);

    const visibleUsers = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return enabledUsers;
        return (users || [])
            .filter(u => [u.fullName, u.userEmail, u.username].some(v => (v || '').toLowerCase().includes(term)))
            .slice(0, MAX_RESULTS);
    }, [users, enabledUsers, search]);

    const toggleAccess = async (user) => {
        const newValue = user.canSeeTeamAndCareer !== true;
        setProcessingId(user.id);
        try {
            // El listener de AdminContext refresca la lista solo.
            await setDoc(doc(db, 'users', user.id, 'profile', 'data'), { canSeeTeamAndCareer: newValue }, { merge: true });
            Swal.fire({
                title: '¡Actualizado!',
                text: newValue
                    ? `${user.fullName || user.userEmail} ahora ve Equipo de Trabajo y Recorrido Gerencial.`
                    : `Equipo de Trabajo y Recorrido Gerencial ocultos para ${user.fullName || user.userEmail}.`,
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } catch (error) {
            console.error('Error al actualizar acceso a Equipo/Plan de Carrera:', error);
            Swal.fire('Error', 'No se pudo actualizar el acceso.', 'error');
        } finally {
            setProcessingId(null);
        }
    };

    return (
        <div className="career-table-card team-career-access">
            <div className="team-career-access__header">
                <div>
                    <h3><Users size={18} /> Acceso a Equipo y Recorrido Gerencial</h3>
                    <p>Estas secciones están ocultas para los usuarios. Habilitá acá a quienes las pueden ver ({enabledUsers.length} habilitados).</p>
                </div>
                <div className="search-wrapper">
                    <input
                        type="text"
                        placeholder="Buscar usuario por nombre o email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>
            <div className="team-career-access__list">
                {visibleUsers.length === 0 ? (
                    <p className="team-career-access__empty">
                        {search.trim() ? 'No se encontraron usuarios.' : 'Nadie habilitado todavía. Buscá un usuario para habilitarlo.'}
                    </p>
                ) : visibleUsers.map(user => {
                    const enabled = user.canSeeTeamAndCareer === true;
                    return (
                        <div key={user.id} className="team-career-access__row">
                            <div className="team-career-access__user">
                                <strong>{user.fullName || user.username || 'Sin nombre'}</strong>
                                <span>{user.userEmail || user.id}</span>
                            </div>
                            <button
                                type="button"
                                className={`team-career-access__toggle${enabled ? ' is-enabled' : ''}`}
                                onClick={() => toggleAccess(user)}
                                disabled={processingId === user.id}
                                title={enabled ? 'Ocultar Equipo y Recorrido Gerencial' : 'Mostrar Equipo y Recorrido Gerencial'}
                            >
                                {enabled ? <Eye size={14} /> : <EyeOff size={14} />}
                                {enabled ? 'Visible' : 'Oculto'}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default TeamCareerAccess;
